import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Edit, ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkflowActivity from '../components/workflow/WorkflowActivity';
import { toast } from 'react-hot-toast';
import GeneralDataView from '../components/MaterialCreation/views/GeneralDataView';
import BasicDataView from '../components/MaterialCreation/views/BasicDataView';
import SalesView from '../components/MaterialCreation/views/SalesView';
import MRPView from '../components/MaterialCreation/views/MRPView';
import ForecastingView from '../components/MaterialCreation/views/ForecastingView';
import ProductionResourcesToolsView from '../components/MaterialCreation/views/ProductionResourcesToolsView';
import PlantDataStorageView from '../components/MaterialCreation/views/PlantDataStorageView';
import AccountingView from '../components/MaterialCreation/views/AccountingView';
import { useSelectedPlants } from '../hooks/useSelectedPlants';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaterialView = () => {
  const navigate = useNavigate();
  const { materialNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [materialTypeInfo, setMaterialTypeInfo] = useState(null);
  const [selectedViews, setSelectedViews] = useState({
    general_data: true,
    basic_data: true,
  });
  const [activeTab, setActiveTab] = useState('general_data');
  const [formData, setFormData] = useState({
    general_data: {},
    basic_data: {},
    sales_data: [],
    mrp: {},
    forecasting: {},
    plant_data_storage: [],
    production_resources_tools: {},
    accounting: {}
  });
  
  const selectedPlants = useSelectedPlants(formData);

  useEffect(() => {
    if (materialNumber) {
      loadMaterial();
    }
  // eslint-disable-next-line
  }, [materialNumber]);

  const loadMaterial = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/materials/${materialNumber}`);
      const material = response.data;
      
      const typeResponse = await axios.get(`${BACKEND_URL}/api/material-types/${material.material_type}`);
      setMaterialTypeInfo({
        materialType: typeResponse.data,
        materialNumber: material.material_number
      });
      
      const loadedFormData = {
        general_data: {
          industry_sector: material.industry_sector || ''
        },
        basic_data: {
          description: material.description || '',
          base_uom: material.base_uom || '',
          material_group: material.material_group || '',
          weight_unit: material.weight_unit || '',
          gen_item_cater: material.gen_item_cater || '',
          division: material.division || ''
        },
        sales_data: material.sales_data || [],
        mrp: material.mrp || {},
        forecasting: material.forecasting || {},
        plant_data_storage: material.plant_data_storage && typeof material.plant_data_storage === 'object' ? 
          (Array.isArray(material.plant_data_storage) ? 
            material.plant_data_storage : 
            Object.values(material.plant_data_storage).map(plantData => {
              return {
                plant_id: plantData.plant_id,
                purchasing_group: plantData.purchasing_group || '',
                storage_location_id: plantData.storage_location || '',
                issue_storage_location: plantData.issue_storage_location || '',
                unit_of_issue: plantData.unit_of_issue || '',
                batch_management: plantData.batch_management || false,
                negative_stocks_in_plant: plantData.negative_stocks_in_plant || false,
                cost_center: plantData.cost_center || '',
                mrp_controller: plantData.mrp_controller || '',
                abc_indicator: plantData.abc_indicator || '',
                reorder_point: plantData.reorder_point || null,
                safety_stock: plantData.safety_stock || null,
                max_stock_level: plantData.max_stock_level || null
              };
            })
          ) : [],
        production_resources_tools: material.production_resources_tools || {},
        accounting: material.accounting || {}
      };
      
      setFormData(loadedFormData);
      
      const views = {
        general_data: true,
        basic_data: true,
        sales: material.sales_data && material.sales_data.length > 0,
        mrp: material.mrp && Object.keys(material.mrp).length > 0,
        forecasting: material.forecasting && Object.keys(material.forecasting).length > 0,
        plant_data_storage: material.plant_data_storage && Object.keys(material.plant_data_storage).length > 0,
        production_resources_tools: material.production_resources_tools && Object.keys(material.production_resources_tools).length > 0,
        accounting: material.accounting && Object.keys(material.accounting).length > 0
      };
      setSelectedViews(views);
      setActiveTab('general_data');
      
    } catch (error) {
      console.error('Error loading material:', error);
      toast.error('Failed to load material');
      navigate('/materials');
    } finally {
      setLoading(false);
    }
  };

  // Plants are derived via useSelectedPlants(formData) hook above

  const handleEdit = () => {
    navigate(`/materials/edit/${materialNumber}`);
  };

  const handleBack = () => {
    navigate('/view-materials');
  };

  const renderTabNavigation = () => {
    const tabs = [
      { key: 'general_data', label: 'General Data', mandatory: true },
      { key: 'basic_data', label: 'Basic Data', mandatory: true },
      { key: 'sales', label: 'Sales' },
      { key: 'mrp', label: 'MRP' },
      { key: 'forecasting', label: 'Forecasting' },
      { key: 'plant_data_storage', label: 'Plant Data / Storage' },
      { key: 'production_resources_tools', label: 'Production Resources / Tools' },
      { key: 'accounting', label: 'Accounting' },
    ];

    const visibleTabs = tabs.filter(tab => selectedViews[tab.key]);

    return (
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <div className="flex space-x-1">
          {visibleTabs.map(tab => {
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                  ${isActive 
                    ? 'border-orange-600 text-orange-600 bg-orange-50' 
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                {tab.label}
                {tab.mandatory && <span className="ml-1 text-xs text-orange-600">*</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Dummy setFormData that does nothing (for read-only mode)
  const noOpSetFormData = () => {};

  const renderTabContent = () => {
    // Pass readOnly=true and a no-op setFormData to prevent any edits
    switch (activeTab) {
      case 'general_data':
        return (
          <GeneralDataView
            formData={formData}
            setFormData={noOpSetFormData}
            materialTypeInfo={materialTypeInfo}
            readOnly={true}
          />
        );
      
      case 'basic_data':
        return (
          <BasicDataView
            formData={formData}
            setFormData={noOpSetFormData}
            readOnly={true}
          />
        );

      case 'sales':
        return (
          <SalesView
            formData={formData}
            setFormData={noOpSetFormData}
            readOnly={true}
          />
        );

      case 'mrp':
        return (
          <MRPView
            formData={formData}
            setFormData={noOpSetFormData}
            selectedPlants={selectedPlants}
            readOnly={true}
          />
        );

      case 'forecasting':
        return (
          <ForecastingView
            formData={formData}
            setFormData={noOpSetFormData}
            selectedPlants={selectedPlants}
            readOnly={true}
          />
        );

      case 'production_resources_tools':
        return (
          <ProductionResourcesToolsView
            formData={formData}
            setFormData={noOpSetFormData}
            selectedPlants={selectedPlants}
            readOnly={true}
          />
        );

      case 'plant_data_storage':
        return (
          <PlantDataStorageView
            formData={formData}
            setFormData={noOpSetFormData}
            selectedPlants={selectedPlants}
            readOnly={true}
          />
        );

      case 'accounting':
        return (
          <AccountingView
            formData={formData}
            setFormData={noOpSetFormData}
            selectedPlants={selectedPlants}
            readOnly={true}
          />
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data</h3>
            <p className="text-sm text-gray-600 max-w-md">
              No data available for this section.
            </p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          <span className="ml-3 text-lg">Loading material...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">View Material</h1>
              <p className="text-sm text-gray-600 mt-1">
                Material: <span className="font-semibold text-orange-600">{materialNumber}</span>
                {materialTypeInfo && (
                  <span className="ml-4">
                    Type: <span className="font-semibold">{materialTypeInfo.materialType?.description || materialTypeInfo.materialType?.material_type_id}</span>
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to List
              </Button>
              <Button
                onClick={handleEdit}
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
              >
                <Edit className="w-4 h-4" />
                Edit Material
              </Button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Tab Navigation */}
          {renderTabNavigation()}

          {/* Tab Content - Wrapped in read-only container */}
          <div className="p-6">
            <div className="read-only-form">
              <style>{`
                .read-only-form input,
                .read-only-form select,
                .read-only-form textarea,
                .read-only-form button:not(.tab-button),
                .read-only-form [role="combobox"],
                .read-only-form [role="checkbox"],
                .read-only-form [role="switch"] {
                  pointer-events: none !important;
                  opacity: 0.7;
                  background-color: #f9fafb !important;
                  cursor: not-allowed !important;
                }
                .read-only-form input:focus,
                .read-only-form select:focus,
                .read-only-form textarea:focus {
                  outline: none !important;
                  box-shadow: none !important;
                  ring: none !important;
                }
                .read-only-form button[class*="add"],
                .read-only-form button[class*="remove"],
                .read-only-form button[class*="delete"],
                .read-only-form button svg[class*="plus"],
                .read-only-form button svg[class*="trash"],
                .read-only-form button svg[class*="x"] {
                  display: none !important;
                }
              `}</style>
              {renderTabContent()}
            </div>
          </div>
          <div className="px-6 pb-6">
            <WorkflowActivity doctype="material" recordId={materialNumber} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MaterialView;
